import { capitalize } from 'lodash';
import { TextField } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import { ExternalProductMatching } from '../../interfaces/SynchronizationSummary/ExternalProductMatching';
import { PartnerPlatform } from '../../interfaces/PartnerPlatform/PartnerPlatform';
import { Pagination } from '../../interfaces/Pagination';
import { TableColumn } from '../../components/DataTable/TableColumn';
import { TextColumn } from '../../components/DataTable/TextColumn';
import { DataTable } from '../../components/DataTable/DataTable';
import { sharedColors } from '../../utils/Style';
import { ProductIdentifierUpdate } from '../../interfaces/ProductIdentifiers/ProductIdentifiers';
import { getMatchingFieldName } from './utils';
import { PriceColumn } from '../../components/DataTable/PriceColumn';

type ExternalProductMatchingTableProps = {
  externalProductMatches: ExternalProductMatching[];
  updates: ProductIdentifierUpdate[];
  onUpdateIdentifier: (
    row: ExternalProductMatching,
    updatedValue: string,
  ) => void;
  partnerPlatform: PartnerPlatform;
  totalCount: number;
  pagination: Pagination;
  onPagination: (newPagination: Pagination) => void;
};

export const ExternalProductMatchingTable = ({
  externalProductMatches,
  updates,
  onUpdateIdentifier,
  partnerPlatform,
  totalCount,
  pagination,
  onPagination,
}: ExternalProductMatchingTableProps) => {
  const matchFieldName = getMatchingFieldName(partnerPlatform);

  const columns: TableColumn<ExternalProductMatching>[] = [
    {
      id: 'product-id',
      name: 'ID',
      accessor: (row) => <TextColumn value={row.productID?.toString() ?? ''} />,
    },
    {
      id: 'product-name',
      name: 'İsim',
      accessor: (row) => <TextColumn value={row.productName ?? ''} />,
    },
    {
      id: 'option-value-id',
      name: 'Varyant',
      accessor: (row) => <TextColumn value={row.optionValueID ?? ''} />,
    },
    {
      id: 'existing-id',
      name: `${matchFieldName} (NNET)`,
      accessor: (row) => {
        if (!row.productID) return null;

        const update = updates.find(
          (x) =>
            x.productID === row.productID &&
            x.optionValueID === row.optionValueID,
        );
        const isUpdated = !!update && update.value !== row.existingID;

        return (
          <TextField
            size='small'
            sx={{
              width: 250,
              backgroundColor: isUpdated ? sharedColors.blue1 : undefined,
            }}
            value={update?.value ?? row.existingID ?? ''}
            onChange={(ev) => onUpdateIdentifier(row, ev.target.value)}
            InputProps={
              isUpdated
                ? {
                    endAdornment: <EditIcon fontSize='small' color='primary' />,
                  }
                : undefined
            }
          />
        );
      },
    },
    {
      id: 'external-id',
      name: `${matchFieldName} (${capitalize(partnerPlatform)})`,
      accessor: (row) => <TextColumn value={row.externalID ?? ''} />,
    },
    {
      id: 'base-price',
      name: 'Baz Fiyat (NNET)',
      accessor: (row) =>
        Number.isFinite(row.basePrice) ? (
          <PriceColumn value={row.basePrice!} />
        ) : null,
    },
    {
      id: 'list-price',
      name: 'Komisyonlu Fiyat (NNET)',
      accessor: (row) =>
        Number.isFinite(row.listPrice) ? (
          <PriceColumn value={row.listPrice!} />
        ) : null,
    },
    {
      id: 'external-list-price',
      name: `Fiyat (${capitalize(partnerPlatform)})`,
      accessor: (row) =>
        Number.isFinite(row.externalListPrice) ? (
          <PriceColumn
            value={row.externalListPrice!}
            icon={
              Number.isFinite(row.listPrice) &&
              row.listPrice !== row.externalListPrice ? (
                <WarningIcon color='warning' />
              ) : undefined
            }
          />
        ) : null,
    },
  ];

  return (
    <DataTable
      columns={columns}
      totalCount={totalCount}
      rows={externalProductMatches}
      limit={pagination.pageSize}
      setLimit={(newLimit) => onPagination({ page: 0, pageSize: newLimit })}
      page={pagination.page}
      setPage={(newPage) => onPagination({ ...pagination, page: newPage })}
      rowColorizer={(row) =>
        !row.existingID || !row.externalID
          ? sharedColors.statusRedLightest
          : sharedColors.statusGreenLightest
      }
    />
  );
};
